import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Collapsible } from '@liveauctioneers/hammer-ui-core/collapsible';
import { useFeatureFlag } from '@liveauctioneers/hammer-ui-core/featureFlags/hooks';
import { Crown } from '@liveauctioneers/hammer-ui-core/icons';
import { H2, H3, H6 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useState } from 'react';

import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { useAppSelector } from '@/redux/hooks';
import { getIsVipConfirmationEligible, getUserFullName } from '@/redux/modules/account/user/user.selectors';
import { FeatureFlagKeys } from '@/types/FeatureFlags';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { BenefitItem, BENEFITS } from './BenfitItem';

const NARROW_VISIBLE_COUNT = 2;
const WIDE_VISIBLE_COUNT = 3;

const WIDE_LAYOUT_MIN_WIDTH = 1249;

const benefitListClassName = 'grid w-full grid-cols-1 gap-x-xl mdMin:grid-cols-2 lgMin:grid-cols-3! gap-y-lg';

export const VipBenefits = () => {
    const formatMessage = useAppFormatMessage();
    const bidderName = useAppSelector(getUserFullName);
    const isBackendEligible = useAppSelector(getIsVipConfirmationEligible);
    const isNarrowLayout = useIsNarrowerThan(WIDE_LAYOUT_MIN_WIDTH);
    const isVipBenefitsEnabled = useFeatureFlag(FeatureFlagKeys.VipBenefits);
    const [isExpanded, setIsExpanded] = useState(false);

    const visibleCount = isNarrowLayout ? NARROW_VISIBLE_COUNT : WIDE_VISIBLE_COUNT;
    const hiddenCount = BENEFITS.length - visibleCount;

    if (!isVipBenefitsEnabled || !isBackendEligible) {
        return null;
    }

    return (
        <div className="mt-sm flex flex-col gap-sm bg-background-tertiary p-md">
            <div className="flex min-w-0 items-center gap-sm">
                <div className="w-fit shrink-0 rounded-full border-2 border-warning-600 bg-warning-100 p-xs">
                    <Crown
                        color="warning"
                        size="lg"
                        style="duotone"
                    />
                </div>
                <div className="min-w-0">
                    {bidderName && <H2 className="wrap-break-word">{bidderName}</H2>}
                    <H6 color="warning">{formatMessage(['vipBenefits', 'nameTitle'])}</H6>
                </div>
            </div>
            <hr className="border-border-secondary" />
            <H3 className="text-text-primary">{formatMessage(['vipBenefits', 'benefits', 'title'])}</H3>
            <div className={benefitListClassName}>
                {BENEFITS.map((benefit, index) => {
                    if (index < visibleCount) {
                        return (
                            <BenefitItem
                                benefit={benefit}
                                key={benefit.key}
                            />
                        );
                    }

                    return (
                        <Collapsible
                            className={cn('min-w-0', !isExpanded && 'hidden')}
                            expanded={isExpanded}
                            key={benefit.key}
                            timing={400}
                        >
                            <BenefitItem benefit={benefit} />
                        </Collapsible>
                    );
                })}
            </div>
            {hiddenCount > 0 && (
                <Button
                    className="mt-xs self-start"
                    onClick={() => {
                        setIsExpanded((prevExpanded) => !prevExpanded);
                    }}
                    size="sm"
                    variant="outlined"
                >
                    {isExpanded
                        ? formatMessage(['vipBenefits', 'showLess'])
                        : formatMessage(['vipBenefits', 'showMore'], { values: { count: hiddenCount } })}
                </Button>
            )}
        </div>
    );
};
